import axiosClient from './axios.client';

const BASE = '/dashboard-configs';

export const recordAppUsage = async (appId, event, metadata = {}) => {
  try {
    await axiosClient.post(`${BASE}/usage`, { appId, event, metadata });
    return { error: null };
  } catch (e) {
    console.warn('recordAppUsage:', e);
    return { error: e?.message };
  }
};

export const getAppAnalytics = async (id) => {
  try {
    const data = await axiosClient.get(`${BASE}/${id}/analytics`);
    return { data, error: null };
  } catch (e) {
    return { data: null, error: e?.message };
  }
};

export const listDashboardConfigs = async () => {
  try {
    const data = await axiosClient.get(BASE);
    const list = Array.isArray(data) ? data : data?.data ?? [];
    return { data: list, error: null };
  } catch (e) {
    console.error('listDashboardConfigs:', e);
    return { data: [], error: e?.message || 'Failed to list dashboards' };
  }
};

export const getDashboardConfig = async (id) => {
  try {
    const data = await axiosClient.get(`${BASE}/${id}`);
    return { data: data ?? null, error: null };
  } catch (e) {
    console.error('getDashboardConfig:', e);
    return { data: null, error: e?.message || 'Failed to get dashboard' };
  }
};

/** Public fetch — no auth. Use for /p/:id (embed, new tab). */
export const getDashboardConfigPublic = async (id) => {
  try {
    const data = await axiosClient.get(`${BASE}/public/${id}`);
    return { data: data ?? null, error: null };
  } catch (e) {
    console.error('getDashboardConfigPublic:', e);
    const errMsg = e?.message || e?.error || 'Failed to load app';
    return { data: null, error: errMsg };
  }
};

export const createDashboardConfig = async (payload) => {
  try {
    const data = await axiosClient.post(BASE, payload);
    return { data: data ?? null, error: null };
  } catch (e) {
    console.error('createDashboardConfig:', e);
    return { data: null, error: e?.message || 'Failed to create dashboard' };
  }
};

export const updateDashboardConfig = async (id, payload) => {
  try {
    const data = await axiosClient.put(`${BASE}/${id}`, payload);
    return { data: data ?? null, error: null };
  } catch (e) {
    const errMsg = typeof e?.message === 'string' ? e.message : e?.error || 'Failed to update dashboard';
    console.error('updateDashboardConfig:', e);
    return { data: null, error: errMsg };
  }
};

export const deleteDashboardConfig = async (id) => {
  try {
    await axiosClient.delete(`${BASE}/${id}`);
    return { error: null };
  } catch (e) {
    console.error('deleteDashboardConfig:', e);
    return { error: e?.message || 'Failed to delete dashboard' };
  }
};

/** Authenticated: load persisted interview chat/transcript for this dashboard app. */
export const getInterviewAppSession = async (appId) => {
  try {
    const data = await axiosClient.get(`${BASE}/${encodeURIComponent(appId)}/interview-session`);
    return { data, error: null };
  } catch (e) {
    const errMsg = typeof e?.message === 'string' ? e.message : e?.error || 'Failed to load interview session';
    return { data: null, error: errMsg };
  }
};

/** Authenticated: upsert interview session (requires owner or editor on the app). */
export const putInterviewAppSession = async (appId, body) => {
  try {
    const data = await axiosClient.put(`${BASE}/${encodeURIComponent(appId)}/interview-session`, body);
    return { data, error: null };
  } catch (e) {
    const errMsg = typeof e?.message === 'string' ? e.message : e?.error || 'Failed to save interview session';
    return { data: null, error: errMsg };
  }
};

export const listAppShares = async (appId) => {
  try {
    const data = await axiosClient.get(`/dashboard-configs/${appId}/shares`);
    return { data: Array.isArray(data) ? data : data?.data ?? data ?? [], error: null };
  } catch (e) {
    return { data: [], error: e?.message };
  }
};

export const addAppShare = async (appId, { targetUserId, role }) => {
  try {
    const data = await axiosClient.post(`/dashboard-configs/${appId}/shares`, { targetUserId, role });
    return { data, error: null };
  } catch (e) {
    return { data: null, error: e?.message };
  }
};

export const removeAppShare = async (appId, shareUserId) => {
  try {
    await axiosClient.delete(`/dashboard-configs/${appId}/shares/${shareUserId}`);
    return { error: null };
  } catch (e) {
    return { error: e?.message };
  }
};

/** Duplicate an app by fetching and creating a new one with copied config */
export const duplicateDashboardConfig = async (id) => {
  try {
    const { data: original, error: getErr } = await getDashboardConfig(id);
    if (getErr || !original) return { data: null, error: getErr || 'App not found' };
    const copy = {
      name: `${original.name || 'Untitled App'} (Copy)`,
      description: original.description || '',
      defaultFlowId: original.defaultFlowId || null,
      layout: original.layout || 'three-panel',
      chatPosition: original.chatPosition || 'center',
      theme: original.theme,
      panels: (original.panels || []).map((p) => ({
        ...p,
        id: `${p.position}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      })),
      gridLayout: original.gridLayout,
    };
    const { data, error } = await createDashboardConfig(copy);
    return { data, error };
  } catch (e) {
    console.error('duplicateDashboardConfig:', e);
    return { data: null, error: e?.message || 'Failed to duplicate' };
  }
};
